import React from "react";
import { isSafeHref } from "../../services/safe-href";
import { plainText } from "../../atoms/text/text";
import { PackageShopCta } from "./package-shop-cta.client";
import { TileLabels } from "./tile-labels";
import styles from "./package-tile.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * One Signature package tile in the grid header — GH2, GH3, GH9. A row component does not also
 * render the list it sits in (plan §1.7A), so the header owns how many tiles there are and in what
 * order; this owns what one tile looks like.
 */

/** R1-04.1 — how many glow steps `package-tile.module.css` publishes. */
export const ACCENT_STEPS = 4;

export interface PackageTileProps {
  /** GH1/GH2 — the package's content-managed label, looked up by
   *  `billingProductCode` before it reaches here. Rendered as plain text. */
  label: string;
  /** GH2 — the content-managed channel count, e.g. "125+ channels". */
  countText?: string;
  /** GH2 — the shop-page destination. The button renders only when this is
   *  a safe http(s) URL; an unsafe or absent one draws no button at all. */
  ctaHref?: string;
  /** GH2 — the button's wording, from the package's authored or package-config CTA. Absent, no
   *  button. */
  ctaLabel?: string;
  /** The button's accessible name, as authored. Absent, the visible wording names it. */
  ctaAriaLabel?: string;
  /**
   * Analytics position for the CTA, so every tile on a page reports the same region. Defaults to
   * `LINK_POSITION_BODY` inside the atom.
   */
  linkPosition?: string;
  /** This tile's column position, **starting at 0**. */
  index?: number;
  className?: string;
}

function PackageTileImpl({
  label,
  countText,
  ctaHref,
  ctaLabel,
  ctaAriaLabel,
  linkPosition,
  index = 0,
  className
}: PackageTileProps) {
  /* The live feed ships markup inside package labels the same way it does inside channel names
     (GR9); rendering the raw string as a React child would escape it and show the tags. */
  const name = plainText(label);
  const count = countText ? plainText(countText) : undefined;
  /* Trimmed, like the label above it, but not for the gate's sake: `isSafeHref` refuses a
     whitespace-only value on its own (WAT-12420 runs its dead-link test on `href.trim()`), so GH2's
     "set up or it is not" holds either way. */
  const askedHref = (ctaHref ?? "").trim();
  const href = isSafeHref(askedHref) ? askedHref : "";

  /* R1-04.1 — the column's glow step, 1-based because it names a design step rather than an array
     position. Clamped rather than wrapped: a fifth column repeating the first column's pale glow
     would read as the start of a second ramp. */
  const accent = Math.min(Math.max(index, 0), ACCENT_STEPS - 1) + 1;
  const ctaText = plainText((ctaLabel ?? "").trim());

  return (
    <div
      className={[styles.tile, className].filter(Boolean).join(" ")}
      data-cl-accent={accent}
    >
      <TileLabels countText={count} name={name} />
      {href !== "" && ctaText !== "" && (
        <PackageShopCta
          ariaLabel={ctaAriaLabel}
          href={href}
          index={index}
          label={ctaText}
          linkPosition={linkPosition}
          packageName={name}
        />
      )}
    </div>
  );
}
export const PackageTile = withSafeRender(PackageTileImpl, "PackageTile");

export default PackageTile;
